import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "@ferrivia/i18n";
import { Text } from "@ferrivia/ui";
import { dayMs, segments, type TrendAxis } from "../LabTrendChart";
import s from "./LabTrendCombinedChart.module.css";

/* The client PWA's trend figure: ferritin, CRP and haemoglobin as three lines
   in ONE plot area, on ONE shared scale, with their measured values plotted
   as measured — nothing here scales, indexes, normalises or smooths a number,
   and there is deliberately no reference range in these props at all. See
   "The software does not interpret" in the root CLAUDE.md and REQ-MDR-002:
   charting values over time is permitted display-only, and what that
   permission rests on is that the chart draws numbers on a printed scale and
   does nothing else with them.

   The clinic console's `LabTrendChart` stacks the same three analytes as
   three panels with three scales, which is the right shape for a clinician
   reading each analyte's granularity. This one is the shape Ville chose for
   the client, prototyped in `product/prototypes/client-journey.html`: one
   figure the client reads at a glance. The consequence of one shared scale is
   that CRP (≈0.5–2 mg/L) sits near the baseline and haemoglobin (≈120–140
   g/L) up near the top — expected, not a bug, and the reason every value is
   also available as an exact number, in the tooltip.

   Note what "one shared scale" buys, beyond matching the prototype: the
   alternative — three series each stretched to its own invisible scale inside
   one frame — would draw three lines whose heights mean three different
   things without saying so. One printed scale for the whole figure keeps the
   picture honest at the cost of two flat-ish lines.

   The legend sits above the figure and names each line with no number next
   to it; the measured values live in a tooltip that appears once a draw is
   tapped or moved to with the arrow keys (Ville, 2026-09-07) — freeing the
   space below the figure for a second chart landing there next. */
export interface LabTrendCombinedPoint {
  /** "YYYY-MM-DD", the draw's collection date. */
  collectedOn: string;
  value: number;
}

export interface LabTrendCombinedSeries {
  analyteCode: string;
  label: string;
  unit: string;
  /** Which of the three chart-series tokens paints this line. Series colour
      identifies *which analyte* and nothing else — no value is ever coloured
      by where it falls. */
  seriesIndex: 1 | 2 | 3;
  /** Ordered oldest-first. A draw that carried no measurement for this
      analyte is simply absent, which breaks the line rather than
      interpolating a number nobody measured. */
  points: LabTrendCombinedPoint[];
}

export interface LabTrendCombinedChartProps {
  series: LabTrendCombinedSeries[];
  /** The one scale every series is drawn against, with the ticks to print.
      Built by `trendAxis` from the measured values themselves and nothing
      else — never from a reference range, which would make the scale an
      implicit comparison against it. The ticks carry no unit because the
      three series do not share one; the units are in the tooltip, next to
      the numbers they belong to. */
  axis: TrendAxis;
  /** Every distinct draw date across all series, oldest first — the time
      axis, and the columns selection snaps to. */
  dates: string[];
  /** Short form, for the two ends of the time axis. */
  formatAxisDate: (collectedOn: string) => string;
  /** Long form, for the tooltip's heading. */
  formatDrawDate: (collectedOn: string) => string;
  /** Fired when the reader moves the selection to another draw. Carries
      nothing: a selection is an interaction, not a measurement. */
  onDrawSelected?: () => void;
}

const VIEW_W = 320;
const VIEW_H = 172;
const PAD_L = 32;
const PAD_R = 12;
const PAD_T = 12;
const PAD_B = 24;

export function LabTrendCombinedChart({
  series,
  axis,
  dates,
  formatAxisDate,
  formatDrawDate,
  onDrawSelected,
}: LabTrendCombinedChartProps) {
  const { t } = useTranslation("labs");
  const rootRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [selected, setSelected] = useState<string | null>(null);
  const [tooltipNudgePx, setTooltipNudgePx] = useState(0);

  // A tap or click anywhere outside the figure closes the tooltip — the only
  // way to dismiss it, since the tooltip itself is `pointer-events: none`
  // and a tap on the figure instead moves the selection to that point.
  useEffect(() => {
    if (selected === null) {
      return;
    }
    function handlePointerDown(event: PointerEvent) {
      const root = rootRef.current;
      if (root && event.target instanceof Node && !root.contains(event.target)) {
        setSelected(null);
      }
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [selected]);

  // Nudges the tooltip back inside the chart's own frame when its centred
  // position would run it past the left or right edge — a draw near either
  // end of the time axis. Clamped to the frame, not the window: a scrolling
  // ancestor (the results panel's overflow) clips at its own edge.
  useLayoutEffect(() => {
    const tooltip = tooltipRef.current;
    if (selected === null || tooltip === null) {
      setTooltipNudgePx(0);
      return;
    }
    const rect = tooltip.getBoundingClientRect();
    // jsdom (component tests) never lays out a real box — every rect comes
    // back zeroed, which would otherwise read as "past the left edge" and
    // nudge forever.
    if (rect.width === 0 && rect.height === 0) {
      return;
    }
    const frame = rootRef.current?.getBoundingClientRect();
    const minLeft = (frame?.left ?? 0) + 8;
    const maxRight = (frame?.right ?? window.innerWidth) - 8;
    const unnudgedLeft = rect.left - tooltipNudgePx;
    const unnudgedRight = rect.right - tooltipNudgePx;
    let nudge = 0;
    if (unnudgedRight > maxRight) {
      nudge = maxRight - unnudgedRight;
    } else if (unnudgedLeft < minLeft) {
      nudge = minLeft - unnudgedLeft;
    }
    if (nudge !== tooltipNudgePx) {
      setTooltipNudgePx(nudge);
    }
  }, [selected, tooltipNudgePx]);

  const latest = dates.length > 0 ? dates[dates.length - 1]! : null;
  // The selected column starts on the newest draw, whether or not its
  // tooltip has been opened yet — the enlarged marker and dashed column need
  // somewhere to point at from the first render. Deriving it rather than
  // storing it means a refetch that changes the draw list can never leave
  // the cursor on a column that is no longer drawn.
  const active = selected !== null && dates.includes(selected) ? selected : latest;

  // Below two draws there is nothing to draw a line between: one lab result is
  // one data point. The caller renders no trend section at all in that case;
  // this is the second guard, so the component cannot be made to draw an axis
  // with a single dot on it.
  if (dates.length < 2 || series.length === 0 || active === null) {
    return null;
  }

  const plotW = VIEW_W - PAD_L - PAD_R;
  const plotH = VIEW_H - PAD_T - PAD_B;
  const first = dayMs(dates[0]!);
  const xSpan = dayMs(dates[dates.length - 1]!) - first || 1;
  const ySpan = axis.max - axis.min || 1;

  const x = (collectedOn: string) => PAD_L + ((dayMs(collectedOn) - first) / xSpan) * plotW;
  const y = (value: number) => PAD_T + plotH - ((value - axis.min) / ySpan) * plotH;

  // Arrow consts rather than function declarations: a declaration is hoisted
  // above the guard that proved `active` is not null, so TypeScript will not
  // carry the narrowing into one.
  const select = (date: string | null) => {
    if (date === null || date === active) {
      return;
    }
    setSelected(date);
    onDrawSelected?.();
  };

  const nearestDate = (clientX: number): string | null => {
    const frame = frameRef.current;
    if (frame === null) {
      return null;
    }
    const rect = frame.getBoundingClientRect();
    const local = ((clientX - rect.left) / (rect.width || VIEW_W)) * VIEW_W;
    let best: string | null = null;
    let bestDistance = Infinity;
    for (const date of dates) {
      const distance = Math.abs(x(date) - local);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = date;
      }
    }
    return best;
  };

  const step = (delta: number) => {
    const index = dates.indexOf(active);
    const next = dates[Math.min(dates.length - 1, Math.max(0, index + delta))];
    select(next ?? null);
  };

  return (
    <div className={s.root} ref={rootRef}>
      <ul className={s.legend}>
        {series.map((entry) => (
          <li key={entry.analyteCode} className={s.legendItem}>
            <i className={s.swatch} data-series={entry.seriesIndex} aria-hidden="true" />
            <Text as="span" size="sm" className={s.legendLabel}>
              {entry.label}
            </Text>
          </li>
        ))}
      </ul>

      <div
        ref={frameRef}
        className={s.frame}
        role="group"
        tabIndex={0}
        aria-label={t("combinedTrend.frameLabel", "Mittaukset ajan mittaan. Selaa mittauskertoja nuolinäppäimillä.")}
        onMouseMove={(e) => select(nearestDate(e.clientX))}
        onTouchStart={(e) => select(nearestDate(e.touches[0]!.clientX))}
        onTouchMove={(e) => select(nearestDate(e.touches[0]!.clientX))}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") {
            e.preventDefault();
            step(-1);
          }
          if (e.key === "ArrowRight") {
            e.preventDefault();
            step(1);
          }
        }}
      >
        <svg className={s.plot} viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} role="img" aria-labelledby={titleId}>
          <title id={titleId}>
            {t("combinedTrend.figureLabel", "{{analytes}} — {{count}} mittauskertaa", {
              analytes: series.map((entry) => entry.label).join(", "),
              count: dates.length,
            })}
          </title>

          {axis.ticks.map((tick) => (
            <g key={tick}>
              <line x1={PAD_L} y1={y(tick)} x2={VIEW_W - PAD_R} y2={y(tick)} className={s.grid} />
              <text x={PAD_L - 6} y={y(tick)} className={s.tick} textAnchor="end" dominantBaseline="middle">
                {tick}
              </text>
            </g>
          ))}

          {/* The selected draw's column, behind the lines so it never sits on
              top of a marker. */}
          <line x1={x(active)} y1={PAD_T - 6} x2={x(active)} y2={PAD_T + plotH} className={s.column} />

          {series.map((entry) =>
            segments(entry.points, dates).map((segment, i) => (
              <polyline
                key={`${entry.analyteCode}-${i}`}
                className={s.line}
                data-series={entry.seriesIndex}
                points={segment.map((point) => `${x(point.collectedOn)},${y(point.value)}`).join(" ")}
              />
            )),
          )}

          {series.map((entry) =>
            entry.points.map((point) => (
              <circle
                key={`${entry.analyteCode}-${point.collectedOn}`}
                cx={x(point.collectedOn)}
                cy={y(point.value)}
                // The selected draw's markers are the only ones enlarged, and
                // that is a cursor: it marks which column the tooltip is
                // reading, never anything about the values in it.
                r={point.collectedOn === active ? 4.4 : 3}
                className={s.marker}
                data-series={entry.seriesIndex}
              />
            )),
          )}

          <text x={PAD_L} y={VIEW_H - 6} className={s.axisDate} textAnchor="start">
            {formatAxisDate(dates[0]!)}
          </text>
          <text x={VIEW_W - PAD_R} y={VIEW_H - 6} className={s.axisDate} textAnchor="end">
            {formatAxisDate(dates[dates.length - 1]!)}
          </text>
        </svg>

        {/* Hidden until a draw is actually tapped, dragged to or moved to with
            the arrow keys: unlike the legend above, this is the exact
            measured numbers, and it stays on screen (rather than clearing on
            release) once shown, so a tap-and-let-go still leaves the reading
            visible. */}
        {selected !== null && (
          <div
            ref={tooltipRef}
            className={s.tooltip}
            aria-live="polite"
            style={{
              left: `${(x(active) / VIEW_W) * 100}%`,
              transform: `translateX(-50%) translateX(${tooltipNudgePx}px)`,
            }}
          >
            <Text as="span" size="xs" className={s.tooltipDate}>
              {formatDrawDate(active)}
            </Text>
            {series.map((entry) => {
              const point = entry.points.find((p) => p.collectedOn === active);
              return (
                <span key={entry.analyteCode} className={s.tooltipRow}>
                  <i className={s.swatch} data-series={entry.seriesIndex} aria-hidden="true" />
                  <Text as="span" size="xs" className={s.tooltipLabel}>
                    {entry.label}
                  </Text>
                  {/* The measurement recorded on that draw, or an explicit
                      "not measured" — never a value read off the line. */}
                  <Text as="span" size="xs" weight="medium" className={s.tooltipValue}>
                    {point ? `${point.value} ${entry.unit}` : t("trend.notMeasured", "ei mitattu")}
                  </Text>
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
